import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Iframe from "react-iframe";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import MyLoader from "../Spinner/MyLoader";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/certificate.css";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const DOCUMENT_TYPE = "leaving_certificate";
const PREVIEW_LOADER_MS = 2500;

const PANEL_STYLE = {
  margin: "0 20px 20px",
  padding: "0",
};

const PAGE_TITLE_BAR_STYLE = {
  width: "100%",
  background: `linear-gradient(135deg, ${COLORS.primary_color_shade6} 0%, ${COLORS.primary_color_shade8} 100%)`,
  padding: "16px 24px",
  borderRadius: "8px 8px 0 0",
  boxShadow: `0 2px 4px ${COLORS.primary_color_shade3}`,
  marginTop: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: "10px",
};

const PAGE_TITLE_STYLE = {
  fontSize: "22px",
  fontWeight: "600",
  color: "#fff",
  margin: 0,
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const IFRAME_STYLE = {
  border: "none",
  backgroundColor: "transparent",
  overflow: "hidden",
  display: "block",
  width: "1200px",
  maxWidth: "100%",
};

const PREVIEW_OVERLAY_STYLE = {
  position: "absolute",
  inset: 0,
  zIndex: 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(255,255,255,0.55)",
  pointerEvents: "none",
};

//Created By Cursor
function normalizeDir(dir) {
  if (!dir) return "";
  return dir.endsWith("/") ? dir : `${dir}/`;
}

//Created By Cursor
function buildTcPrintUrl(student, sample) {
  const schoolCode = SessionManager.getSchoolCode();
  const studentId = student?.student_id || student?.student_enrollment_no || "";
  const sessionId =
    student?.session_id ||
    SessionManager.getCurrentSession()?.[0]?.session_id ||
    "";
  const fileName = sample?.file_name || "";
  const fileDirectory = normalizeDir(sample?.file_directory || "");
  if (!fileName || !studentId) return "";

  const params = new URLSearchParams();
  params.set("student_ids", JSON.stringify([studentId]));
  params.set("class_id", student?.passed_class_id || student?.class_id || "");
  params.set("section_id", "");
  params.set("session_id", sessionId);
  params.set("sample_id", sample?.sample_id || "");
  params.set("selection_type", "student");
  params.set("system_code", process.env.REACT_APP_SYSTEM_CODE || "");
  params.set("school_code", schoolCode || "");
  return `${process.env.REACT_APP_SERVER_NAME}${fileDirectory}${fileName}?${params.toString()}`;
}

function PageHeader({ onBack }) {
  return (
    <div
      className="content-header"
      style={{ background: COLORS.primary_color_shade5 }}
    >
      <div className="leftside-content-header">
        <ul className="breadcrumbs">
          <li>
            <i
              className="fa fa-home"
              aria-hidden="true"
              style={{ color: COLORS.white }}
            />
            <span style={{ color: COLORS.white }}>TC Preview</span>
          </li>
        </ul>
      </div>
      <div className="rightside-content-header">
        <BackButton onClick={onBack} />
      </div>
    </div>
  );
}

const TransferCertificatePreview = () => {
  const history = useHistory();
  const location = useLocation();
  const [loggedIn] = useState(!!localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [fileUrl, setFileUrl] = useState("");
  const [iframeKey, setIframeKey] = useState(0);
  const [height, setHeight] = useState("900px");

  const student = useMemo(
    () => location.state?.student || null,
    [location.state]
  );
  const studentName = student?.stu_name || student?.name || "Student";
  const certificateNo = student?.certificate_no || "";

  const handleBack = useCallback(() => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      history.push("/AdminMain/TransferCertificate");
    }
  }, [history]);

  //Created By Cursor
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  //Created By Cursor — load printable sample and build iframe URL
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!student?.student_id && !student?.student_enrollment_no) {
        setLoading(false);
        setPreviewLoading(false);
        setFileUrl("");
        myToast.notify_warning("Student details missing for TC preview.");
        return;
      }

      try {
        setLoading(true);
        setPreviewLoading(true);
        const schoolCode = SessionManager.getSchoolCode();
        const res = await axios.get(`${API_BASE()}getPrintableSample.php`, {
          params: {
            school_code: schoolCode,
            document_type: DOCUMENT_TYPE,
          },
        });

        if (
          res.data?.api_status !== "OK" ||
          !Array.isArray(res.data.data) ||
          !res.data.data.length
        ) {
          throw new Error(
            res.data?.api_remark || "No TC printable sample configured."
          );
        }

        const sample = res.data.data[0];
        const url = buildTcPrintUrl(student, sample);
        if (!url) {
          throw new Error("Unable to build leaving certificate URL.");
        }
        if (!cancelled) {
          setFileUrl(url);
          setIframeKey((k) => k + 1);
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setFileUrl("");
          myToast.notify_warning(
            error.message || "Failed to load leaving certificate preview."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [student]);

  //Created By Cursor — listen for printable height messages
  useEffect(() => {
    const onMessage = (event) => {
      if (event.data?.iframeHeight) {
        setHeight(`${event.data.iframeHeight}px`);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  //Created By Cursor — clear overlay if iframe onLoad never fires
  useEffect(() => {
    if (!previewLoading || !fileUrl) return undefined;
    const timer = window.setTimeout(() => {
      setPreviewLoading(false);
    }, PREVIEW_LOADER_MS);
    return () => window.clearTimeout(timer);
  }, [previewLoading, fileUrl, iframeKey]);

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  if (!student) {
    return <Redirect to="/AdminMain/TransferCertificate" />;
  }

  return (
    <div className="content" style={{ background: COLORS.white }}>
      <PageHeader onBack={handleBack} />

      <div
        className="animated fadeInUp bg_page_white_rounded box_shadow"
        style={PANEL_STYLE}
      >
        <div style={PAGE_TITLE_BAR_STYLE}>
          <h3 style={PAGE_TITLE_STYLE}>
            <i
              className="fa fa-file-text-o"
              aria-hidden="true"
              style={{ color: "#fff" }}
            />
            Leaving Certificate
          </h3>
          <span
            style={{
              color: "rgba(255,255,255,0.95)",
              fontSize: "13px",
              fontWeight: "700",
            }}
          >
            {studentName}
            {certificateNo ? ` · ${certificateNo}` : ""}
          </span>
        </div>

        <div style={{ padding: "16px 20px 24px", background: COLORS.white }}>
          {loading ? (
            <MyLoader />
          ) : fileUrl ? (
            <div
              className="preview-frame"
              style={{
                overflowX: "auto",
                overflowY: "hidden",
                position: "relative",
                borderRadius: "10px",
                border: `2px solid ${COLORS.primary_color_shade3}`,
                background: COLORS.white,
              }}
            >
              {previewLoading ? (
                <div style={PREVIEW_OVERLAY_STYLE}>
                  <MyLoader />
                </div>
              ) : null}
              <Iframe
                key={iframeKey}
                url={fileUrl}
                width="100%"
                height={height}
                id="iFrameTransferCertificate"
                className="bg_card_primary_outline"
                display="block"
                position="relative"
                frameBorder="0"
                scrolling="yes"
                style={IFRAME_STYLE}
                onLoad={() => setPreviewLoading(false)}
              />
            </div>
          ) : (
            <div className="text-center py-5">
              <h5
                style={{ color: COLORS.primary_color_shade8, fontWeight: "700" }}
              >
                Preview unavailable
              </h5>
              <p style={{ color: COLORS.primary_color_shade6, marginBottom: 0 }}>
                Template or student details are missing.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

TransferCertificatePreview.displayName = "TransferCertificatePreview";

export default memo(TransferCertificatePreview);
